// 扩展 HTMLElement 类型
declare global {
  interface HTMLElement {
    $jdTrack?: (trackData: any) => void
  }
}

export function trackAnalyse({
  target,
  currentTarget,
  detail,
  eventContent: inEventContent,
}: {
  target: HTMLElement
  currentTarget: HTMLElement
  detail: number
  eventContent?: string
}): {
  eventName?: string
  eventContent?: string
  componentName?: string
  eventLabel?: string
  path?: string[]
  eventProperty?: Record<string, any>
} | null {
  if (detail === 0) {
    return null
  }
  try {
    let eventName: string | undefined,
      eventContent = inEventContent,
      componentName: string | undefined,
      eventLabel: string | undefined,
      componentElement: HTMLElement | undefined
    let eventProperty: Record<string, any> | undefined
    let path: string[] | undefined
    for (const el of bubble(target, currentTarget)) {
      const {
        componentName: elComponentName,
        eventName: elEventName,
        eventContent: elEventContent,
        eventLabel: elEventLabel,
        eventLabelSelector,
        eventContentParentSelector,
        eventContentSelector,
      } = el.dataset
      // 收集事件属性
      forOwn(el.dataset, (v, k) => {
        if (typeof k === 'string' && k.indexOf('trackProperty') === 0) {
          eventProperty = {
            ...eventProperty,
            // 首字母转为小写
            [k
              .replace('trackProperty', '')
              .replace(/./, (g) => g.toLowerCase())]: v,
          }
        }
      })
      // 组件内容，不会被外层覆盖
      if (eventContent == null && elEventContent) {
        eventContent =
          elEventContent === 'true'
            ? el.innerText?.replace(/\n/g, ' - ')
            : elEventContent
      }
      if (eventContent == null && eventContentSelector) {
        let pEl: HTMLElement = el
        while (
          eventContentParentSelector &&
          pEl &&
          !pEl.className.includes(eventContentParentSelector.slice(1))
        ) {
          const parent = pEl.parentNode
          if (parent instanceof HTMLElement) {
            pEl = parent
          } else {
            break
          }
        }
        pEl ??= el
        const contentElement = pEl.querySelector(eventContentSelector)
        eventContent =
          contentElement instanceof HTMLElement
            ? contentElement.innerText?.replace(/\n/g, ' - ')
            : undefined
      }
      // 组件名，不会被外层覆盖（优先取内层）
      if (elComponentName && !componentName) {
        componentName = elComponentName
        componentElement = el
      }
      // 事件名，不会被外层覆盖
      if (elEventName && !eventName) {
        eventName = elEventName
      }
      // label，不会被外层覆盖
      if (elEventLabel && !eventLabel) {
        eventLabel = elEventLabel
      }
      if (!elEventLabel && eventLabelSelector) {
        const labelElement = el.querySelector(eventLabelSelector)
        eventLabel =
          labelElement instanceof HTMLElement
            ? labelElement.innerText
            : undefined
      }
    }
    // 寻找路径
    if (componentElement) {
      for (const el of bubble(componentElement, currentTarget)) {
        const { eventContainer } = el.dataset // 是否是事件容器
        if (eventContainer) {
          // 优先：沿事件目标到事件容器的祖先链构建大纲，避免重复并更贴近实际触发路径
          let ancestorPath: Array<{ outline: string; level: number; label?: string }> = []
          const seenKeys = new Set<string>()
          for (const element of bubble(target, el)) {
            const { outline, outlineLabelSelector } = element.dataset
            if (outline) {
              const level = [...bubble(element, el)].length // 大纲层级
              const label = outlineLabelSelector
                ? (() => {
                    // 优先在目标到当前大纲元素的链条上找匹配项
                    let candidate: HTMLElement | null = null
                    for (const t of bubble(target, element)) {
                      if (
                        typeof (t as any).matches === 'function' &&
                        t.matches(outlineLabelSelector)
                      ) {
                        candidate = t
                        break
                      }
                    }
                    if (!candidate) {
                      const fallback =
                        element.querySelector(outlineLabelSelector)
                      candidate =
                        fallback instanceof HTMLElement ? fallback : null
                    }
                    const labelEl = candidate ?? element
                    return labelEl.innerText
                  })()
                : element.innerText // 大纲文案
              const key = `${outline}|${level}`
              if (!seenKeys.has(key)) {
                seenKeys.add(key)
                // 从目标往上，使用unshift保持从容器到目标的顺序
                ancestorPath.unshift({ outline, level, label })
              }
            }
          }
          if (ancestorPath.length > 0) {
            path = ancestorPath.map((x) => x.label).filter(Boolean) as string[]
            break
          }
          // 退化：遍历容器内元素，取包含目标的outline，保持原有重构逻辑，防止路径缺失
          let currentPath: Array<{
            outline: string
            level: number
            label?: string
          }> = []
          const nodeIterator = document.createNodeIterator(el, 1)
          let node = nodeIterator.nextNode()
          while (node !== null) {
            const element = node as HTMLElement
            const { outline, outlineLabelSelector } = element.dataset
            if (outline && element.contains(target)) {
              const level = [...bubble(element, el)].length // 大纲层级
              const label = outlineLabelSelector
                ? (() => {
                    // 在当前大纲元素内定位匹配项（如选中的标签）
                    const inner = element.querySelector(outlineLabelSelector)
                    const candidate: HTMLElement | null =
                      inner instanceof HTMLElement ? inner : null
                    const labelEl = candidate ?? element
                    return labelEl.innerText
                  })()
                : element.innerText // 大纲文案
              // 找到同大纲同层级元素，重构路径
              const index = currentPath.findIndex(
                (x) => x.outline === outline && x.level === level
              )
              if (index !== -1) {
                currentPath = currentPath.slice(0, index)
              }
              currentPath.push({ outline, level, label })
            }
            node = nodeIterator.nextNode()
          }
          path = currentPath.map((x) => x.label).filter(Boolean) as string[]
          break
        }
      }
    }

    return {
      eventName,
      eventContent,
      componentName,
      eventLabel,
      path,
      eventProperty,
    }
  } catch {
    return null
  }
}

export function* bubble(
  element: HTMLElement | null,
  root: HTMLElement | null = null,
  {
    getParent = (el: HTMLElement) => el.parentElement,
  }: { getParent?: (el: HTMLElement) => HTMLElement | null } = {}
): Generator<HTMLElement> {
  while (element != root && element) {
    yield element
    element = getParent(element)
  }
}

export function hasOwnProperty(
  obj: Record<string, any>,
  key: string | symbol
): boolean {
  return Object.prototype.hasOwnProperty.call(obj, key)
}

export function* iterate(
  object: Record<string, any>,
  symbol = false
): Generator<[any, string | symbol]> {
  for (const key in object) {
    if (hasOwnProperty(object, key)) {
      yield [object[key], key]
    }
  }

  if (symbol) {
    for (const key of Object.getOwnPropertySymbols(object)) {
      yield [(object as any)[key], key]
    }
  }
}

export function forOwn(
  object: Record<string, any>,
  callback: (value: any, key: string | symbol) => void,
  symbol = false
) {
  for (const [value, key] of iterate(object, symbol)) {
    callback(value, key)
  }
}
